import React, { useState } from 'react';
import { View, Pressable } from 'react-native';

import Text from '~/client/components/Text';
import { useMusicKit } from '~/client/utils/MusicKitContext';
import isWeb from '~/client/utils/isWeb';

// adds every song on an album to the Apple Music queue, in album order.
// MusicKit JS is web-only, so like the song page's player bar this renders
// nothing elsewhere.
export default function QueueAlbumButton({
  songs,
  textColor,
}: {
  // the album's songs, in the order they should play.
  songs: {
    song_id: string;
    apple_catalog_id?: string | null;
    is_user_favorite?: boolean;
  }[];
  textColor: string;
}) {
  const musicKit = useMusicKit();
  const [isQueueing, setIsQueueing] = useState(false);
  const [queuedCount, setQueuedCount] = useState<number | null>(null);

  // only songs we know Apple Music's catalog id for can be queued.
  const queueableSongs = songs.filter((song) => song.apple_catalog_id);

  if (!isWeb() || !musicKit.isAvailable || queueableSongs.length === 0) {
    return null;
  }

  const handlePress = async () => {
    setIsQueueing(true);
    setQueuedCount(null);
    const queued = await musicKit.queueSongs(
      queueableSongs.map((song) => ({
        catalogId: song.apple_catalog_id as string,
        songMeta: {
          songId: String(song.song_id),
          isFavorite: !!song.is_user_favorite,
        },
      })),
    );
    setIsQueueing(false);
    if (queued) {
      setQueuedCount(queueableSongs.length);
    }
  };

  return (
    <View style={{ marginBottom: 16 }}>
      <Pressable
        disabled={isQueueing}
        onPress={handlePress}
        style={{
          opacity: isQueueing ? 0.5 : 1,
          // @ts-expect-error `cursor` is a web-only style property
          cursor: isQueueing ? 'default' : 'pointer',
        }}
      >
        <Text style={{ color: textColor, fontWeight: 'bold' }}>
          {isQueueing ? 'Adding to Queue…' : '+ Add Album to Queue'}
        </Text>
      </Pressable>
      {queuedCount !== null ? (
        <Text style={{ color: textColor, fontSize: 12, marginTop: 4 }}>
          Added {queuedCount} song{queuedCount === 1 ? '' : 's'} to the queue.
        </Text>
      ) : null}
      {musicKit.error ? (
        <Text style={{ color: textColor, fontSize: 12, marginTop: 4 }}>
          {musicKit.error}
        </Text>
      ) : null}
    </View>
  );
}
